import React from 'react'
import { isNumber } from '@chakra-ui/utils'
import { __DEV__ } from '@mote/helpers/constants'
import { getResourceFile } from '@mote/helpers/misc'

const SAMPLE_RATE = 16000
const AVERAGE_LEVEL = 0.95

function log(...args) {
  if (__DEV__) console.log(...args)
}

/**
 * Modified version of Microsoft Cognitive Services Speech SDK Recorder
 * Source: https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common.browser/PCMRecorder.ts
 */
export const useAudioAnimation = (audioContext, audioStream, options = {}) => {
  const { smoothing = 0, normalize = false, bufferSize = 2048 } = options || {}
  const scriptNodeRef = React.useRef(null)
  const [volume, setVolume] = React.useState(0)

  const processAudio = React.useCallback(buffer => {
    if (!buffer) return

    const sum = buffer.reduce((acc, value) => acc + Math.pow(value, 2), 0)
    const rms = Math.sqrt(sum / buffer.length)

    setVolume(prev => Math.max(rms, prev * AVERAGE_LEVEL) * 3)
  }, [])

  const attachScriptProcessor = React.useCallback(() => {
    log('[useAudioAnimation] Creating Script Node Processor')

    const scriptNode = (() => {
      let newBufferSize = 0
      try {
        return audioContext.createScriptProcessor(newBufferSize, 1, 1)
      } catch (error) {
        newBufferSize = bufferSize
        let audioSampleRate = audioContext.sampleRate
        while (newBufferSize < 16384 && audioSampleRate >= 2 * SAMPLE_RATE) {
          newBufferSize <<= 1
          audioSampleRate >>= 1
        }
        return audioContext.createScriptProcessor(newBufferSize, 1, 1)
      }
    })()
    scriptNode.onaudioprocess = event => {
      const buffer = event.inputBuffer.getChannelData(0)
      processAudio(buffer)
    }
    audioStream.connect(scriptNode)
    scriptNode.connect(audioContext.destination)
    scriptNodeRef.current = scriptNode
  }, [audioContext, audioStream, processAudio, bufferSize])

  const attachWorkletProcessor = React.useCallback(() => {
    const workletNode = new AudioWorkletNode(
      audioContext,
      'mote-amplitude-processor',
      {
        outputChannelCount: [1],
        parameterData: { smoothing },
        processorOptions: {
          normalize,
          smoothing,
          numInputChannels: 1,
          bufferSize
        }
      }
    )
    log('[useAudioAnimation] Creating Worklet Processor')
    workletNode.port.onmessage = event => {
      if (!isNumber(normalize ? event.data?.volNorm : event.data?.volume)) {
        return
      }

      setVolume((normalize ? event.data.volNorm : event.data.volume) * 10)
    }
    audioStream.connect(workletNode)
    workletNode.connect(audioContext.destination)
    scriptNodeRef.current = workletNode
  }, [audioContext, audioStream, smoothing, normalize, bufferSize])

  const beginAnimation = React.useCallback(() => {
    if (!audioStream || !audioContext) return

    if (audioContext.audioWorklet) {
      try {
        attachWorkletProcessor()
      } catch (err) {
        audioContext.audioWorklet
          .addModule(getResourceFile('/worklets/AmplitudeProcessor.js'))
          .then(attachWorkletProcessor)
          .catch(() => {
            attachScriptProcessor()
          })
      }
    } else {
      try {
        attachScriptProcessor()
      } catch (err) {
        log('Unable to start audio worklet node for AudioAnimation:', err)
      }
    }
  }, [audioContext, audioStream, attachScriptProcessor, attachWorkletProcessor])

  const endAnimation = React.useCallback(() => {
    if (scriptNodeRef.current) {
      scriptNodeRef.current.disconnect(audioContext?.destination)
      try {
        scriptNodeRef.current.port.onmessage = null
      } catch (err) {
        scriptNodeRef.current.onaudioprocess = null
      }
      scriptNodeRef.current = null
    }
    setVolume(0)
  }, [audioContext])

  return { beginAnimation, endAnimation, amplitude: volume }
}
